import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import { BRAND } from '../theme'

/**
 * The frame every signed-out screen shares.
 *
 * Extracted from the login screen once there were four of these. Four copies of
 * a branded card is four places to update a logo, and the one that gets missed
 * is always the password screen somebody reaches from an email — the moment they
 * are most entitled to wonder whether the page is genuine.
 */
export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 4,
        /*
         * The brand wash, over the page rather than over a second copy of it.
         *
         * This carried its own base colour — first the page colour opaque, then
         * at 25% alpha. Opaque, it hid the texture the app puts behind
         * everything, so signing in looked like a different product from the one
         * you had just signed into. At 25% it stopped hiding it and started
         * tinting it instead: the page colour laid quarter-strength over itself,
         * which is a muddier version of the same colour and the reason it did
         * not look right.
         *
         * `transparent` is the version that does what both attempts were
         * reaching for. The canvas already carries the colour and the texture,
         * so the wash is the only thing this needs to add.
         */
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'radial-gradient(1200px 600px at 50% -10%, rgba(126,211,72,0.12), transparent)'
            : 'radial-gradient(1200px 600px at 50% -10%, rgba(126,211,72,0.22), transparent)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Stack spacing={1.25} alignItems="center" sx={{ mb: 3 }}>
          <Box
            sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: BRAND, display: 'grid', placeItems: 'center' }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 24, color: '#10230a', lineHeight: 1 }}>T</Typography>
          </Box>
          <Typography variant="h1">TallyWise</Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" align="center">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>

        <Card>
          <CardContent sx={{ p: 3 }}>
            {title ? (
              <Typography variant="h3" sx={{ mb: 2 }}>
                {title}
              </Typography>
            ) : null}
            {children}
          </CardContent>
        </Card>

        {footer ? <Box sx={{ mt: 2.5, textAlign: 'center' }}>{footer}</Box> : null}
      </Box>
    </Box>
  )
}
